import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Session } from '@supabase/supabase-js'
import type { SubscriptionTier } from '@/features/home/queries'
import type { Language } from '@/shared/i18n/types'

/**
 * 'system' follows the OS via prefers-color-scheme and is the default for a new
 * visitor — it leaves `data-theme` OFF the root so the media query in tokens.css
 * decides. 'light' / 'dark' are explicit choices and win in both directions.
 */
export type Theme = 'system' | 'dark' | 'light'

/** Single source of truth for how a Language reaches the DOM. Also used pre-render in main.tsx. */
export function applyLanguage(language: Language): void {
  document.documentElement.setAttribute('lang', language)
}

/** The theme actually painted: resolves 'system' against the OS preference. */
export function resolvedTheme(theme: Theme): 'light' | 'dark' {
  if (theme !== 'system') return theme
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/** Single source of truth for how a Theme reaches the DOM. Also used pre-render in main.tsx. */
export function applyTheme(theme: Theme): void {
  if (theme === 'system') {
    document.documentElement.removeAttribute('data-theme')
  } else {
    document.documentElement.setAttribute('data-theme', theme)
  }
}

export type Toast = {
  id: string
  type: 'goal' | 'info' | 'error' | 'success'
  title: string
  body?: string
  durationMs?: number
}

const UPSELL_SUPPRESS_DAYS = 7

type AppState = {
  session: Session | null
  theme: Theme
  language: Language
  activeLeagueId: string | null
  toasts: Toast[]
  upsellBannerDismissedAt: number | null  // Unix ms — persisted across sessions
  mobileMenuOpen: boolean                 // mobile sidebar drawer — never persisted
}

type AppActions = {
  setSession: (session: Session | null) => void
  setTheme: (theme: Theme) => void
  setLanguage: (language: Language) => void
  setActiveLeagueId: (id: string | null) => void
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
  dismissUpsellBanner: () => void
  setMobileMenuOpen: (open: boolean) => void
}

const initialState: AppState = {
  session: null,
  theme: 'light',
  language: 'pt-BR',
  activeLeagueId: null,
  toasts: [],
  upsellBannerDismissedAt: null,
  mobileMenuOpen: false,
}

export function selectIsUpsellVisible(tier: SubscriptionTier, state: AppState): boolean {
  if (tier !== 'FREE') return false
  if (state.upsellBannerDismissedAt === null) return true
  return Date.now() - state.upsellBannerDismissedAt > UPSELL_SUPPRESS_DAYS * 86_400_000
}

/** Exactly what `partialize` writes to localStorage — the shape `migrate` returns. */
type PersistedState = {
  theme:                   Theme
  language:                Language
  activeLeagueId:          string | null
  upsellBannerDismissedAt: number | null
}

export const useAppStore = create<AppState & AppActions>()(
  persist(
    (set) => ({
      ...initialState,
      setSession: (session) => set({ session }),
      setTheme: (theme) => {
        applyTheme(theme)
        set({ theme })
      },
      setLanguage: (language) => {
        applyLanguage(language)
        set({ language })
      },
      setActiveLeagueId: (activeLeagueId) => set({ activeLeagueId }),
      addToast: (toast) =>
        set((state) => ({
          toasts: [...state.toasts, { ...toast, id: crypto.randomUUID() }],
        })),
      removeToast: (id) =>
        set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
      dismissUpsellBanner: () => set({ upsellBannerDismissedAt: Date.now() }),
      setMobileMenuOpen: (mobileMenuOpen) => set({ mobileMenuOpen }),
    }),
    {
      name: 'liga-app',
      // v1 makes light the default for EVERYONE, including accounts that already
      // had 'dark' (the old default) or 'system' persisted. It is a one-time
      // reset, not a lock: anything the user picks after this is stored at v1
      // and kept. Bump again only for another deliberate reset.
      version: 1,
      migrate: (persisted, fromVersion): PersistedState => {
        const stored = persisted as Partial<PersistedState> | undefined
        const state: PersistedState = {
          theme:                   stored?.theme ?? initialState.theme,
          language:                stored?.language ?? initialState.language,
          activeLeagueId:          stored?.activeLeagueId ?? null,
          upsellBannerDismissedAt: stored?.upsellBannerDismissedAt ?? null,
        }
        return fromVersion < 1 ? { ...state, theme: 'light' } : state
      },
      partialize: (state) => ({
        theme: state.theme,
        language: state.language,
        activeLeagueId: state.activeLeagueId,
        upsellBannerDismissedAt: state.upsellBannerDismissedAt,
      }),
    },
  ),
)

// Named selectors — never destructure the whole store
export const selectSession = (s: AppState & AppActions) => s.session
export const selectTheme = (s: AppState & AppActions) => s.theme
export const selectActiveLeagueId = (s: AppState & AppActions) => s.activeLeagueId
export const selectToasts = (s: AppState & AppActions) => s.toasts
export const selectMobileMenuOpen = (s: AppState & AppActions) => s.mobileMenuOpen
